/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type { DeploymentGetResponse } from '@modules/cloud-api/v1/types'
import type { ApmCluster, ApmId, RegionId } from '@modules/ui-types'

import { getRegionId } from '@/lib/stackDeployments/selectors/fundamentals'

import { FETCH_STACK_DEPLOYMENT } from '../../constants/actions'

import createApm from './createApm'

import type { ReduxState } from '@/types/redux'
import type { FetchDeploymentAction } from '@/actions/stackDeployments/types'

export interface State {
  [apmId: string]: ApmCluster
}

type Action = FetchDeploymentAction

function createDescriptor(regionId: string, apmId: string) {
  return `${regionId}/${apmId}`
}

function apmReducer(apm: ApmCluster | undefined, action: Action): ApmCluster | undefined {
  if (action.type === FETCH_STACK_DEPLOYMENT) {
    const stackDeployment = action.payload as DeploymentGetResponse
    const apmCluster = stackDeployment.resources.apm[0]

    if (apmCluster) {
      const { id, info } = apmCluster
      const regionId = getRegion(action)
      const selfUrl = getSelfUrl(regionId, id)
      return createApm({
        regionId,
        apmId: id,
        selfUrl,
        source: info,
        stackDeployment: action.payload,
      })
    }
  }

  return apm
}

export default function apmsReducer(state: State = {}, action: Action) {
  if (action.type === FETCH_STACK_DEPLOYMENT && !action.error && action.payload) {
    const regionId = getRegion(action)
    const apmId = getClusterId()
    return {
      ...state,
      [createDescriptor(regionId, apmId)]: apmReducer(_getApm(state, regionId, apmId), action),
    }
  }

  return state

  function getClusterId() {
    if (
      action.type === FETCH_STACK_DEPLOYMENT &&
      !action.error &&
      action.payload &&
      action.payload.resources.apm[0]
    ) {
      return action.payload.resources.apm[0].id
    }

    return '-'
  }
}

function getRegion(action: Action) {
  const { regionId } = action.meta

  if (regionId) {
    return regionId
  }

  // assume Stack deployments API action
  const stackDeploymentsAction = action as FetchDeploymentAction
  return getRegionId({ deployment: stackDeploymentsAction.payload as DeploymentGetResponse })
}

function _getApm(state: State, regionId: RegionId, apmId: ApmId): ApmCluster | undefined {
  return state[createDescriptor(regionId, apmId)]
}

function getSelfUrl(regionId: string, id: string): string {
  return `/api/v1/regions/${regionId}/clusters/apm/${id}`
}

export const getApm = (state: ReduxState, regionId: RegionId, apmId: ApmId) =>
  _getApm(state.apms, regionId, apmId)
